import { Link, useNavigate, useParams } from "react-router-dom";
import { bateriaApi } from "../../api/endpoints.js";
import type { BateriaResumen } from "../../api/types.js";
import { AdultScreen, AdultHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { Loader } from "../../components/Loader.js";
import { ErrorBanner, InfoBanner } from "../../components/Form.js";
import { useQuery } from "../../hooks/useQuery.js";
import { esReanudable } from "../../eval/bateria.js";
import { useT, type Translate } from "../../i18n/index.js";
import styles from "./Parent.module.css";

/** Estados con pre-informe disponible (procesado). */
function tienePreinforme(estado: BateriaResumen["estado"]): boolean {
  return estado === "procesada" || estado === "inconcluyente";
}

/** Texto humano del estado de la batería para el padre (nunca clínico). */
function estadoHumano(b: BateriaResumen, t: Translate): string {
  switch (b.estado) {
    case "creada":
    case "mic_check":
    case "en_progreso":
      return t("history.estado.enProgreso", {
        hechos: b.ejerciciosCompletados,
        total: b.ejerciciosTotal,
      });
    case "procesando":
      return t("history.estado.procesando");
    case "procesada":
    case "inconcluyente":
      return t("history.estado.preinformeListo");
    case "completada":
      return t("history.estado.completada");
    case "suspendida":
      return t("history.estado.pausada");
    case "cancelada":
      return t("history.estado.cancelada");
  }
}

export function ChildHistoryPage() {
  const t = useT();
  const navigate = useNavigate();
  const { childId = "" } = useParams();
  const { data, loading, error, reload } = useQuery(
    () => bateriaApi.listar(childId),
    [childId],
  );

  return (
    <AdultScreen>
      <AdultHeader title={t("history.title")} />
      <Content>
        {loading ? <Loader label={t("common.loading")} /> : null}
        {error ? (
          <>
            <ErrorBanner message={error} />
            <Button variant="ghost" onClick={reload}>
              {t("common.retry")}
            </Button>
          </>
        ) : null}

        {data && data.length === 0 ? (
          <InfoBanner>{t("history.empty")}</InfoBanner>
        ) : null}

        {data?.map((b) => (
          <Card key={b.id}>
            <div className={styles.consentHead}>
              <b className={styles.kidName}>
                {t("history.banda", { banda: b.banda })}
                {b.modoPrueba ? <span className={styles.qaTag}>{t("history.qaBadge")}</span> : null}
              </b>
              <span className={styles.consentVersion}>
                {t("history.createdAt", { fecha: new Date(b.createdAt).toLocaleDateString() })}
              </span>
            </div>
            <p style={{ marginTop: 6 }}>{estadoHumano(b, t)}</p>
            <div className={styles.actions}>
              {esReanudable(b.estado) ? (
                <Button onClick={() => navigate(`/padre/nino/${childId}/evaluar`)}>
                  {t("history.resume")}
                </Button>
              ) : null}
              {tienePreinforme(b.estado) ? (
                <Button onClick={() => navigate(`/padre/reporte/${b.id}`)}>
                  {t("history.viewPre")}
                </Button>
              ) : null}
            </div>
          </Card>
        ))}

        <Link to="/padre" style={{ fontWeight: 700 }}>
          ← {t("common.back")}
        </Link>
      </Content>
    </AdultScreen>
  );
}
